import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PortalShell from "@/components/PortalShell";
import { Avatar, StatusPill, rupee, Stars } from "@/components/Bits";
import { toast } from "sonner";
import { Plus, Star } from "lucide-react";

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: "Home" },
  { key: "bookings", label: "My Bookings", icon: "CalendarDays" },
  { key: "profiles", label: "Care Profiles", icon: "UserRound" },
  { key: "messages", label: "Messages", icon: "MessageSquare" },
  { key: "payments", label: "Payments", icon: "CreditCard" },
  { key: "settings", label: "Settings", icon: "Settings" },
];

export default function SeekerPortal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("dashboard");
  const [bookings, setBookings] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [payments, setPayments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [reviewFor, setReviewFor] = useState(null);

  const load = useCallback(() => {
    api.get("/bookings").then((r) => setBookings(r.data));
    api.get("/care-profiles").then((r) => setProfiles(r.data));
    api.get("/payments").then((r) => setPayments(r.data));
    api.get("/messages/cg-000421").then((r) => setMessages(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const titles = { dashboard: "Dashboard", bookings: "My Bookings", profiles: "Care Profiles", messages: "Messages", payments: "Payments", settings: "Settings" };

  const updateStatus = async (id, status) => {
    try { await api.put(`/bookings/${id}/status`, { status }); toast.success(`Booking ${status.toLowerCase()}`); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const pay = (id) => navigate(`/checkout/${id}`);

  const activeBookings = useMemo(() => bookings.filter((b) => ["Confirmed", "In Progress", "Requested"].includes(b.status)), [bookings]);
  const active_count = activeBookings.length;

  return (
    <PortalShell nav={NAV} active={active} setActive={setActive} title={titles[active]} accent="coral">
      {active === "dashboard" && (
        <div className="space-y-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Active bookings" value={active_count} />
            <Stat label="Total sessions" value={bookings.length} />
            <Stat label="Avg carer rating" value="4.9 ★" />
            <Stat label="NRI Mode" value={user?.is_nri ? "ON" : "—"} />
          </div>
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
            <div className="card-bmc p-5">
              <h3 className="font-display font-bold mb-4">Active bookings</h3>
              <div className="space-y-3">
                {activeBookings.map((b) => (
                  <div key={b.id} className="flex items-center gap-3 p-3 bg-ivory-2 rounded-r">
                    <Avatar initials={b.carer_name.split(" ").map((s) => s[0]).slice(0, 2).join("")} size={42} />
                    <div className="flex-1 min-w-0"><p className="font-semibold text-sm">{b.carer_name}</p><p className="text-xs text-ink-soft">{b.role} · {b.date} · {b.location}</p></div>
                    <StatusPill status={b.status} />
                  </div>
                ))}
                {active_count === 0 && <p className="text-sm text-ink-soft">No active bookings.</p>}
              </div>
            </div>
            <div className="card-bmc p-5">
              <h3 className="font-display font-bold mb-4">Care updates from Kavitha</h3>
              <div className="space-y-2">
                {messages.map((m) => (
                  <div key={m.id} className="bg-sage-soft text-sage-deep text-sm rounded-2xl rounded-bl-sm px-3 py-2 max-w-[90%]">{m.text}</div>
                ))}
                {messages.length === 0 && <p className="text-sm text-ink-soft">No updates yet.</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {active === "bookings" && (
        <div className="card-bmc p-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-ink-soft border-b border-line">{["Booking", "Carer", "Service", "Shift", "Date", "Amount", "Status", "Actions"].map((h) => <th key={h} className="p-3 font-semibold">{h}</th>)}</tr></thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-b border-line-2" data-testid={`booking-row-${b.id}`}>
                  <td className="p-3 font-mono text-xs">{b.id}</td>
                  <td className="p-3">{b.carer_name}</td>
                  <td className="p-3">{b.service_type}</td>
                  <td className="p-3 text-ink-soft">{b.shift}</td>
                  <td className="p-3 text-ink-soft">{b.date}</td>
                  <td className="p-3 font-semibold">{rupee(b.amount)}</td>
                  <td className="p-3"><StatusPill status={b.status} /></td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      {b.status === "Requested" && b.payment_status === "Pending" && <button onClick={() => pay(b.id)} className="text-xs text-coral font-semibold" data-testid={`pay-${b.id}`}>Pay</button>}
                      {b.status === "Completed" && <button onClick={() => setReviewFor(b)} className="text-xs text-sage-deep font-semibold" data-testid={`review-${b.id}`}>Review</button>}
                      {["Requested", "Confirmed"].includes(b.status) && <button onClick={() => updateStatus(b.id, "Cancelled")} className="text-xs text-coral-deep" data-testid={`cancel-${b.id}`}>Cancel</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {active === "profiles" && (
        <div className="grid md:grid-cols-2 gap-4">
          {profiles.map((p) => (
            <div key={p.id} className="card-bmc p-5">
              <div className="flex justify-between items-start">
                <div><h3 className="font-display font-bold">{p.name}</h3><p className="text-sm text-ink-soft">{p.relationship} · Age {p.age}</p></div>
                <StatusPill status="Active" />
              </div>
              <div className="text-sm text-ink-soft mt-3 space-y-1">
                <p>📍 {p.location} {p.pin && `— ${p.pin}`}</p>
                <p>🚶 {p.mobility}</p>
                <p>🩺 {(p.conditions || []).join(", ")}</p>
                <p>👤 Preferred: {p.pref_gender} · {(p.languages || []).join(", ")}</p>
                <p>💊 {p.medications}</p>
                <p>📞 {p.emergency_contact}</p>
              </div>
            </div>
          ))}
          <CareProfileForm onCreated={load} />
        </div>
      )}

      {active === "messages" && (
        <div className="card-bmc p-5 max-w-2xl">
          <div className="bg-coral-soft text-coral-deep text-xs rounded-r p-3 mb-4">💬 Messages are also delivered via WhatsApp to your registered number.</div>
          <h3 className="font-display font-bold mb-3">Kavitha Reddy</h3>
          <div className="space-y-2">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender === "seeker" ? "justify-end" : "justify-start"}`}>
                <div className={`text-sm rounded-2xl px-3 py-2 max-w-[80%] ${m.sender === "seeker" ? "bg-coral text-white" : "bg-ivory-2"}`}>{m.text}</div>
              </div>
            ))}
          </div>
          <MessageBox onSent={load} />
        </div>
      )}

      {active === "payments" && (
        <div>
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <Stat label="Total spent" value={rupee(payments.reduce((s, p) => s + (p.amount || 0), 0))} />
            <Stat label="Payments" value={payments.length} />
            <Stat label="NRI total" value={user?.is_nri ? "$151" : "—"} />
          </div>
          <div className="card-bmc p-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-ink-soft border-b border-line">{["Date", "Booking", "Carer", "Service", "Amount", "NRI", "Method", "Status"].map((h) => <th key={h} className="p-3 font-semibold">{h}</th>)}</tr></thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-b border-line-2">
                    <td className="p-3">{p.date}</td><td className="p-3 font-mono text-xs">{p.booking_id}</td>
                    <td className="p-3">{p.carer_name}</td><td className="p-3 text-ink-soft">{p.service}</td>
                    <td className="p-3 font-semibold">{rupee(p.amount)}</td><td className="p-3 text-ink-soft">{p.nri_amount}</td>
                    <td className="p-3">{p.method}</td><td className="p-3"><StatusPill status={p.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {active === "settings" && (
        <div className="card-bmc p-6 max-w-xl space-y-3">
          <h3 className="font-display font-bold">Personal details</h3>
          {[["Name", user?.name], ["Email", user?.email], ["City", user?.city], ["Phone", user?.phone || "—"], ["NRI", user?.is_nri ? `Yes · ${user?.country || ""}` : "No"]].map(([l, v]) => (
            <div key={l} className="flex justify-between text-sm border-b border-line-2 pb-2"><span className="text-ink-soft">{l}</span><span className="font-medium">{v}</span></div>
          ))}
          <p className="text-xs text-ink-faint pt-2">DPDP consent granted at signup. Notification preferences and saved payment methods managed here.</p>
        </div>
      )}

      {reviewFor && <ReviewModal booking={reviewFor} onClose={() => setReviewFor(null)} onDone={() => { setReviewFor(null); load(); }} />}
    </PortalShell>
  );
}

function Stat({ label, value }) {
  return <div className="card-bmc p-5"><p className="text-sm text-ink-soft">{label}</p><p className="font-display font-bold text-2xl mt-1">{value}</p></div>;
}

function CareProfileForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", relationship: "Father", age: 70, location: "", mobility: "Ambulant", conditions: "", pref_gender: "Any", languages: "" });
  const save = async () => {
    if (!f.name || !f.location) { toast.error("Name and location required"); return; }
    try {
      await api.post("/care-profiles", { ...f, age: +f.age, conditions: f.conditions.split(",").map((s) => s.trim()).filter(Boolean), languages: f.languages.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Profile added"); setOpen(false); setF({ name: "", relationship: "Father", age: 70, location: "", mobility: "Ambulant", conditions: "", pref_gender: "Any", languages: "" }); onCreated();
    } catch (e) { toast.error(apiErr(e)); }
  };
  if (!open) return <button onClick={() => setOpen(true)} className="card-bmc p-5 border-2 border-dashed border-line grid place-items-center text-ink-soft hover:border-coral min-h-[180px]" data-testid="add-profile"><Plus /> Add care profile</button>;
  return (
    <div className="card-bmc p-5 space-y-2">
      <input className="inp2" placeholder="Recipient name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cp-name" />
      <div className="grid grid-cols-2 gap-2">
        <input className="inp2" placeholder="Relationship" value={f.relationship} onChange={(e) => setF({ ...f, relationship: e.target.value })} />
        <input className="inp2" type="number" placeholder="Age" value={f.age} onChange={(e) => setF({ ...f, age: e.target.value })} />
      </div>
      <input className="inp2" placeholder="Location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="cp-location" />
      <input className="inp2" placeholder="Conditions (comma separated)" value={f.conditions} onChange={(e) => setF({ ...f, conditions: e.target.value })} />
      <button onClick={save} className="btn-pill bg-coral text-white w-full justify-center py-2.5 text-sm" data-testid="cp-save">Save profile</button>
      <style>{`.inp2{width:100%;border:1px solid var(--line);border-radius:10px;padding:8px 10px;background:var(--ivory);font-size:13px;outline:none}.inp2:focus{border-color:var(--coral)}`}</style>
    </div>
  );
}

function MessageBox({ onSent }) {
  const [t, setT] = useState("");
  const send = async () => { if (!t.trim()) return; try { await api.post("/messages", { carer_id: "cg-000421", text: t }); setT(""); onSent(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="flex gap-2 mt-4">
      <input value={t} onChange={(e) => setT(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message..." className="flex-1 border border-line rounded-full px-4 py-2 text-sm bg-ivory focus:outline-none focus:border-coral" data-testid="msg-input" />
      <button onClick={send} className="btn-pill bg-coral text-white px-5 py-2 text-sm" data-testid="msg-send">Send</button>
    </div>
  );
}

function ReviewModal({ booking, onClose, onDone }) {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const submit = async () => {
    try { await api.post("/reviews", { carer_id: booking.carer_id, booking_id: booking.id, rating, text }); toast.success("Thank you for your review!"); onDone(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-ink/50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-card rounded-r-lg w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold">Review {booking.carer_name}</h3>
        <div className="flex gap-1 mt-4">
          {[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setRating(n)} data-testid={`star-${n}`}><Star size={28} className={n <= rating ? "text-butter" : "text-line"} fill={n <= rating ? "currentColor" : "none"} /></button>)}
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Share your experience..." className="w-full border border-line rounded-r px-3 py-2 mt-4 text-sm bg-ivory focus:outline-none focus:border-coral resize-none" data-testid="review-text" />
        <button onClick={submit} className="btn-pill bg-coral text-white w-full justify-center py-2.5 mt-3" data-testid="review-submit">Submit review</button>
      </div>
    </div>
  );
}
